import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  adminApi,
  type AdminAppRole,
  type AppUserAccessItem,
  type Permission,
  type Role,
} from '@/services/admin';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/services/api';

/** Trading Agent admin: roles, permissions, and per-app user access (platform admin is at /admin). */
export default function Admin() {
  const { appSlug } = useParams<{ appSlug: string }>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Current app (resolve from appSlug) for "Users with access" section
  const [appId, setAppId] = useState<string | null>(null);
  const [appUserAccess, setAppUserAccess] = useState<AppUserAccessItem[]>([]);
  const [appRoles, setAppRoles] = useState<AdminAppRole[]>([]);
  const [appAccessLoading, setAppAccessLoading] = useState(false);
  const [updatingRoleFor, setUpdatingRoleFor] = useState<string | null>(null);

  // Create permission
  const [newPermissionId, setNewPermissionId] = useState('');
  const [creatingPermission, setCreatingPermission] = useState(false);

  // Create role
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissionIds, setNewRolePermissionIds] = useState<string[]>([]);
  const [creatingRole, setCreatingRole] = useState(false);

  // Edit role (inline)
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRolePermissionIds, setEditRolePermissionIds] = useState<string[]>([]);
  const [savingRole, setSavingRole] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, p] = await Promise.all([adminApi.listRoles(), adminApi.listPermissions()]);
      setRoles(r);
      setPermissions(p);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Resolve app id from slug (e.g. trading-agent) for "Users with access"
  useEffect(() => {
    if (!appSlug) {
      setAppId(null);
      return;
    }
    adminApi
      .listApps()
      .then((apps) => {
        const app = apps.find((a) => a.route_path === appSlug);
        setAppId(app?.id ?? null);
      })
      .catch(() => setAppId(null));
  }, [appSlug]);

  // Load users with access and app roles when app id is known
  useEffect(() => {
    if (!appId) {
      setAppUserAccess([]);
      setAppRoles([]);
      return;
    }
    setAppAccessLoading(true);
    Promise.all([adminApi.listAppUserAccess(appId), adminApi.listAppRoles(appId)])
      .then(([access, rolesList]) => {
        setAppUserAccess(access);
        setAppRoles(rolesList);
      })
      .catch(() => {
        setAppUserAccess([]);
        setAppRoles([]);
      })
      .finally(() => setAppAccessLoading(false));
  }, [appId]);

  const handleUserRoleChange = async (grant: AppUserAccessItem, newAppRoleId: string) => {
    if (grant.app_role?.id === newAppRoleId) return;
    setUpdatingRoleFor(grant.id);
    setError(null);
    try {
      await adminApi.patchUserAppAccess(grant.user_id, grant.id, newAppRoleId);
      if (appId) {
        const next = await adminApi.listAppUserAccess(appId);
        setAppUserAccess(next);
      }
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to update role'));
    } finally {
      setUpdatingRoleFor(null);
    }
  };

  const handleCreatePermission = async (e: React.FormEvent) => {
    e.preventDefault();
    const identifier = newPermissionId.trim();
    if (!identifier) return;
    setCreatingPermission(true);
    setError(null);
    try {
      await adminApi.createPermission(identifier);
      setNewPermissionId('');
      await load();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to create permission'));
    } finally {
      setCreatingPermission(false);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newRoleName.trim();
    if (!name) return;
    setCreatingRole(true);
    setError(null);
    try {
      await adminApi.createRole(name, newRolePermissionIds);
      setNewRoleName('');
      setNewRolePermissionIds([]);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to create role'));
    } finally {
      setCreatingRole(false);
    }
  };

  const startEditRole = (role: Role) => {
    setEditingRoleId(role.id);
    setEditRoleName(role.name);
    setEditRolePermissionIds(role.permission_ids);
  };

  const cancelEditRole = () => {
    setEditingRoleId(null);
    setEditRoleName('');
    setEditRolePermissionIds([]);
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRoleId) return;
    setSavingRole(true);
    setError(null);
    try {
      await adminApi.updateRole(editingRoleId, {
        name: editRoleName.trim(),
        permission_ids: editRolePermissionIds,
      });
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to update role'));
      setSavingRole(false);
      return;
    } finally {
      setSavingRole(false);
    }
    cancelEditRole();
    await load();
  };

  const handleDeleteRole = async (id: string) => {
    if (
      !confirm(
        'Delete this role? Users with this role will keep access until you change their roles.'
      )
    )
      return;
    setError(null);
    try {
      await adminApi.deleteRole(id);
      if (editingRoleId === id) cancelEditRole();
      await load();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to delete role'));
    }
  };

  const toggleEditPermission = (permId: string) => {
    setEditRolePermissionIds((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  /** Toggle permission for the "current" role: the one being edited, or the new role form. */
  const togglePermissionForCurrentRole = (permId: string) => {
    if (editingRoleId) {
      toggleEditPermission(permId);
    } else {
      setNewRolePermissionIds((prev) =>
        prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
      );
    }
  };

  /** Permission IDs for the current context (editing a role or creating new). */
  const currentRolePermissionIds = editingRoleId ? editRolePermissionIds : newRolePermissionIds;
  const currentRoleLabel = editingRoleId
    ? (roles.find((r) => r.id === editingRoleId)?.name ?? 'role')
    : 'New role';

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Trading Agent Admin</h1>
        <p className="text-muted-foreground mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Trading Agent Admin</h1>
        <p className="text-muted-foreground mt-2">
          Manage roles and permissions for the Trading Agent app. To add users or grant app access,
          use <strong>Platform Admin</strong> from the header.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      {/* Permissions: list + create */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Permissions</h2>
        <p className="text-muted-foreground text-sm mb-3">
          Define permission identifiers (e.g. risk:read, agent:control). Then assign them to roles.
        </p>
        <form onSubmit={handleCreatePermission} className="flex flex-wrap items-end gap-3 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">New permission</label>
            <input
              type="text"
              value={newPermissionId}
              onChange={(e) => setNewPermissionId(e.target.value)}
              placeholder="e.g. risk:write"
              disabled={creatingPermission}
              className={cn(
                'flex h-9 w-48 rounded-md border border-input bg-background px-3 py-1 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            />
          </div>
          <Button type="submit" disabled={creatingPermission || !newPermissionId.trim()}>
            Create permission
          </Button>
        </form>
        {permissions.length === 0 ? (
          <p className="text-muted-foreground">No permissions yet. Create one above.</p>
        ) : (
          <div className="rounded-lg border bg-muted/20 p-3">
            <p className="text-sm font-medium mb-1.5">Available permissions</p>
            <p className="text-muted-foreground text-xs mb-2">
              Click to add or remove from <strong>{currentRoleLabel}</strong>. Then create or save
              the role below.
            </p>
            <div className="flex flex-wrap gap-2">
              {permissions.map((p) => {
                const selected = currentRolePermissionIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePermissionForCurrentRole(p.id)}
                    className={cn(
                      'rounded-md border px-2.5 py-1 text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'border-input bg-background hover:bg-muted'
                    )}
                  >
                    {p.identifier}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Roles: create + list with Edit/Delete */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Roles</h2>
        <p className="text-muted-foreground text-sm mb-3">
          Create roles and assign permissions for the Trading Agent app.
        </p>
        {/* Available roles (including default) — visible when creating a role */}
        <div className="mb-4 rounded-md border bg-muted/30 px-3 py-2">
          <span className="text-sm font-medium">Available roles</span>
          <p className="text-muted-foreground text-xs mt-0.5 mb-1.5">
            Existing roles (default: <code className="text-foreground">viewer</code> — used when a
            user has no role or their role is deleted).
          </p>
          {roles.length === 0 ? (
            <p className="text-muted-foreground text-sm">None yet. Create one below.</p>
          ) : (
            <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {roles.map((r) => (
                <li key={r.id}>
                  <code className={r.name === 'viewer' ? 'text-foreground font-medium' : ''}>
                    {r.name}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </div>
        <form onSubmit={handleCreateRole} className="flex flex-wrap items-end gap-3 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Role name</label>
            <input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="e.g. viewer, trader"
              disabled={creatingRole}
              className={cn(
                'flex h-9 w-40 rounded-md border border-input bg-background px-3 py-1 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            />
          </div>
          <div className="space-y-1 shrink-0">
            <span className="text-sm font-medium">Permissions (all available)</span>
            <p className="text-muted-foreground text-xs max-w-[220px]">
              Use the clickable permission chips above to assign to <strong>New role</strong>, then
              create below.
            </p>
          </div>
          <Button type="submit" disabled={creatingRole || !newRoleName.trim()}>
            Create role
          </Button>
        </form>
        {roles.length === 0 ? (
          <p className="text-muted-foreground">No roles defined. Create one above.</p>
        ) : (
          <ul className="border rounded-lg divide-y">
            {roles.map((r) => (
              <li key={r.id} className="divide-y">
                {editingRoleId === r.id ? (
                  <form onSubmit={handleSaveRole} className="p-4 space-y-3 bg-muted/30">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Name</label>
                        <input
                          type="text"
                          value={editRoleName}
                          onChange={(e) => setEditRoleName(e.target.value)}
                          disabled={savingRole}
                          className={cn(
                            'flex h-9 w-40 rounded-md border border-input bg-background px-3 py-1 text-sm',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                          )}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={savingRole || !editRoleName.trim()}>
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditRole}
                          disabled={savingRole}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Permissions (all available)</label>
                      <div className="flex flex-wrap gap-2">
                        {permissions.map((p) => (
                          <label
                            key={p.id}
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm cursor-pointer',
                              editRolePermissionIds.includes(p.id)
                                ? 'border-primary bg-primary/10'
                                : 'border-input bg-background'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={editRolePermissionIds.includes(p.id)}
                              onChange={() => toggleEditPermission(p.id)}
                              disabled={savingRole}
                              className="rounded"
                            />
                            {p.identifier}
                          </label>
                        ))}
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div>
                      <span className="font-medium">{r.name}</span>
                      <span className="ml-2 text-sm text-muted-foreground">
                        {r.permission_ids.length} permission(s)
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditRole(r)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRole(r.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Users with access: see who has access to this app and set their per-app role */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Users with access</h2>
        <p className="text-muted-foreground text-sm mb-3">
          Users who have been granted access to this app (via Platform Admin). Change their
          permission level below.
        </p>
        {!appId ? (
          <p className="text-muted-foreground text-sm">Resolving app…</p>
        ) : appAccessLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : appUserAccess.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No users have been granted access to this app yet. Use <strong>Platform Admin</strong>{' '}
            (header) to grant app access.
          </p>
        ) : (
          <ul className="border rounded-lg divide-y">
            {appUserAccess.map((grant) => (
              <li key={grant.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <span className="font-medium">{grant.user_email ?? grant.user_id}</span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {grant.app_role?.name ?? '—'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={grant.app_role?.id ?? ''}
                    onChange={(e) => handleUserRoleChange(grant, e.target.value)}
                    disabled={updatingRoleFor === grant.id || appRoles.length === 0}
                    className="rounded-md border border-input bg-background px-2 py-1 text-sm min-w-[100px]"
                  >
                    {appRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  {updatingRoleFor === grant.id && (
                    <span className="text-xs text-muted-foreground">Updating…</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
